/**
 * Puantaj servisi.
 * BOSS/Sorumlu personele +/- ödül/ceza puantajı verir, maaşa yansır.
 *
 * 'sistem' rolü: otomatik puantajlar için (geç gelme 4+ tekrar, erken çıkma 4+ tekrar).
 * Kayıt anında personel kendi adına oluşturur — firestore.rules istisnası ile.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import { bildirimGonder, cokluBildirimGonder } from './bildirim-service'
import type { FsTimestamp } from '@/types'

const PUANTAJLAR = 'puantajlar'

export type PuantajVerenRol = 'boss' | 'sorumlu' | 'yonetici' | 'sistem'

export interface Puantaj {
  id: string
  personelId: string
  verenId: string
  verenRol: PuantajVerenRol
  tutar: number  // pozitif veya negatif TL
  aciklama: string
  tarih: FsTimestamp  // hangi gün uygulandı
  ay: string  // YYYY-MM hangi maaş döneminde
  iptal: boolean
  iptalEdenId?: string
  iptalTarihi?: FsTimestamp
  olusturmaTarihi: FsTimestamp
}

export interface PuantajOlusturmaData {
  personelId: string
  tutar: number
  aciklama: string
  ay?: string  // varsayılan: bu ay
}

function yeniId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, PUANTAJLAR)).id
}

function buAyStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export async function puantajOlustur(
  data: PuantajOlusturmaData,
  verenId: string,
  verenRol: PuantajVerenRol
): Promise<string> {
  if (data.tutar === 0) {
    throw new Error('Tutar 0 olamaz.')
  }
  if (!data.aciklama.trim()) {
    throw new Error('Açıklama zorunlu.')
  }

  const db = getFirestoreDb()
  const id = yeniId()

  await setDoc(doc(db, PUANTAJLAR, id), {
    personelId: data.personelId,
    verenId,
    verenRol,
    tutar: data.tutar,
    aciklama: data.aciklama.trim(),
    tarih: nowServer(),
    ay: data.ay ?? buAyStr(),
    iptal: false,
    olusturmaTarihi: nowServer(),
  })

  // Bildirimler — hata ana akışı bozmasın.
  try {
    const pozitif = data.tutar > 0
    const isaret = pozitif ? '+' : '−'
    const mutlak = Math.abs(data.tutar)

    // 1) Personelin kendisine
    await bildirimGonder({
      kullaniciId: data.personelId,
      tipi: 'puantajVerildi',
      aciliyet: 'normal',
      baslik: pozitif ? '⭐ Sana puantaj eklendi' : '⚠️ Puantaj kesintisi',
      mesaj: `${isaret}${mutlak} ₺ — ${data.aciklama.trim()}`,
      link: '/personel',
    })

    // 2) Aktif BOSS'lar + ilgili şube yöneticileri — kendi verdiyse kendisine gitmesin
    const tumSnap = await getDocs(collection(db, 'kullanicilar'))
    const pSnap = await getDoc(doc(db, 'kullanicilar', data.personelId))
    const personelData = pSnap.data() as { isim?: string; subeIdleri?: string[] } | undefined
    const personelIsim = personelData?.isim ?? 'Personel'
    const personelSubeleri = personelData?.subeIdleri ?? []

    const hedefler: string[] = []
    for (const d of tumSnap.docs) {
      if (d.id === verenId || d.id === data.personelId) continue
      const k = d.data() as {
        rol?: string; aktif?: boolean; yoneticiMi?: boolean; subeIdleri?: string[]
      }
      if (k.aktif === false) continue
      // Aktif BOSS
      if (k.rol === 'boss') { hedefler.push(d.id); continue }
      // İlgili şube yöneticisi (personelin şubesini kapsayan)
      if (k.rol === 'personel' && k.yoneticiMi === true) {
        const ortak = (k.subeIdleri ?? []).some((s) => personelSubeleri.includes(s))
        if (ortak) hedefler.push(d.id)
      }
    }

    if (hedefler.length > 0) {
      const verenEtiket = verenRol === 'boss' ? 'BOSS' :
                          verenRol === 'yonetici' || verenRol === 'sorumlu' ? 'Yönetici' :
                          'Sistem'
      await cokluBildirimGonder(hedefler, {
        tipi: 'puantajVerildi',
        aciliyet: 'normal',
        baslik: '📋 Puantaj verildi',
        mesaj: `${verenEtiket} → ${personelIsim}: ${isaret}${mutlak} ₺ (${data.aciklama.trim()})`,
        link: '/boss',
      })
    }
  } catch (e) {
    console.warn('[puantaj] bildirim gönderilemedi:', e)
  }

  return id
}

export async function personelPuantajlariGetir(
  personelId: string,
  ay?: string
): Promise<Puantaj[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, PUANTAJLAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  let liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Puantaj)
  if (ay) liste = liste.filter((p) => p.ay === ay)
  return liste.sort((a, b) => (b.tarih?.toMillis?.() ?? 0) - (a.tarih?.toMillis?.() ?? 0))
}

export async function ayTumPuantajlar(ay: string): Promise<Puantaj[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, PUANTAJLAR), where('ay', '==', ay))
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Puantaj)
  return liste.sort((a, b) => (b.tarih?.toMillis?.() ?? 0) - (a.tarih?.toMillis?.() ?? 0))
}

export async function puantajGetir(id: string): Promise<Puantaj | null> {
  const db = getFirestoreDb()
  const d = await getDoc(doc(db, PUANTAJLAR, id))
  if (!d.exists()) return null
  return { id: d.id, ...d.data() } as Puantaj
}

export async function puantajIptal(
  id: string,
  iptalEdenId: string
): Promise<void> {
  const db = getFirestoreDb()
  await updateDoc(doc(db, PUANTAJLAR, id), {
    iptal: true,
    iptalEdenId,
    iptalTarihi: nowServer(),
  })
}

/**
 * İptal olmayan puantajların toplamı.
 */
export function puantajToplam(puantajlar: Puantaj[]): number {
  return puantajlar
    .filter((p) => !p.iptal)
    .reduce((acc, p) => acc + p.tutar, 0)
}

export function aktifPuantajlar(puantajlar: Puantaj[]): Puantaj[] {
  return puantajlar.filter((p) => !p.iptal)
}
